import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/questionario.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import { useNavigate } from 'react-router-dom';

const QuestionarioManager = () => {
  const navigate = useNavigate();
  const [questionarios, setQuestionarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });

  const [novoQuestionario, setNovoQuestionario] = useState({
    titulo: '',
    descricao: '',
    perfil_alvo: [], // Array para múltiplos perfis
    classificacao: '' // Técnico, Administrativo, Comunidade
  });

  const [perguntas, setPerguntas] = useState([]);
  const [novaPergunta, setNovaPergunta] = useState({
    texto: '',
    tipo: 'Aberta',
    alternativas: ['', ''], // Começa com 2 alternativas vazias
    isCondicional: false, // Nova propriedade
    id_pergunta_condicional: null, // Nova propriedade
    id_alternativa_condicional: null // Nova propriedade
  });
  const [userId, setUserId] = useState(null);
  const [userProfileId, setUserProfileId] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [questionarioEditando, setQuestionarioEditando] = useState(null);
  const [perguntaEditandoId, setPerguntaEditandoId] = useState(null);

  const token = localStorage.getItem('accessToken');
  let canCreateQuestionario = false;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      canCreateQuestionario = decoded.cria_questionario === true;
    } catch (e) {}
  }

  // Mapeamento de tipos exibidos -> enum do banco (tp_pergunta)
  // Valores aceitos: Aberta, Fechada, Escala Likert
  const tipoPerguntaEnumMap = {
    'Aberta': 'Aberta',
    'Fechada': 'Fechada',
    'Escala Likert': 'Escala Likert' // Agora Escala Likert é um tipo próprio no banco
  };

  // Alternativas fixas para Escala Likert
  const alternativasLikert = [
    '1 😞 Muito ruim',
    '2 🙁 Ruim',
    '3 😐 Razoável',
    '4 🙂 Bom',
    '5 😄 Muito bom'
  ];

  // Opções de perfil alvo
  const opcoesPerfil = [
    'Todos',
    'Diretor Escolar',
    'Chefe da Seção Administrativa e Financeira',
    'Chefe da Seção Pedagógica',
    'Secretário Escolar',
    'Aluno',
    'Comunidade Escolar',
    'Equipe Docente',
    'Equipe Administrativa',
    'Equipe Pedagógica (Orientador Educacional, Supervisor Escolar e Bibliotecário)',
    'Superintendente Regional de Educação',
    'Coordenador Administrativo e Financeiro',
    'Gerente de Acompanhamento do Planejamento e Execução dos Repasses Financeiros',
    'Gerente de Fiscalização de Convênios e Fomentos',
    'Gerente Administrativo',
    'Fiscal de Obra',
    'Corpo Técnico',
    'Fornecedor',
    'Prestador de Serviço'
  ];

  // Opções de classificação do questionário
  const opcoesClassificacao = [
    { valor: '', label: 'Selecione uma classificação' },
    { valor: 'Técnico', label: 'Técnico' },
    { valor: 'Administrativo', label: 'Administrativo' },
    { valor: 'Comunidade', label: 'Comunidade' }
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    const id = user?.id_usuario || user?.id || 1;
    const profileId = user?.id_perfil;
    setUserId(id);
    setUserProfileId(profileId);
    carregarQuestionarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mostrarMensagem = (tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  };

  const carregarQuestionarios = async () => {
    try {
      setLoading(true);
      // Usar rota pública para listar questionários
      const response = await api.get('/public/questionario');
      setQuestionarios(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', tratarErroAPI(error, 'carregar', 'questionários'));
    } finally {
      setLoading(false);
    }
  };

  const adicionarPergunta = () => {
    if (!perguntaEditandoId && perguntas.length >= 20) {
      mostrarMensagem('error', 'Limite de 20 perguntas atingido');
      return;
    }

    if (!novaPergunta.texto.trim()) {
      mostrarMensagem('error', 'Digite o texto da pergunta');
      return;
    }

    // Se for fechada ou Escala Likert, validar alternativas
    if (novaPergunta.tipo === 'Fechada' || novaPergunta.tipo === 'Escala Likert') {
      const alternativasValidas = novaPergunta.alternativas.filter(a => a.trim() !== '');
      if (alternativasValidas.length < 2) {
        mostrarMensagem('error', 'Adicione pelo menos 2 alternativas para perguntas fechadas');
        return;
      }
    }

    // Se está editando, atualiza a pergunta existente
    if (perguntaEditandoId) {
      const novasPerguntas = perguntas.map(p => {
        if (p.id === perguntaEditandoId) {
          return {
            id: p.id,
            texto: novaPergunta.texto,
            tipo: novaPergunta.tipo,
            alternativas: (novaPergunta.tipo === 'Fechada' || novaPergunta.tipo === 'Escala Likert')
              ? novaPergunta.alternativas.filter(a => a.trim() !== '')
              : [],
            isCondicional: novaPergunta.isCondicional,
            id_pergunta_condicional: novaPergunta.id_pergunta_condicional,
            id_alternativa_condicional: novaPergunta.id_alternativa_condicional
          };
        }
        return p;
      });
      setPerguntas(novasPerguntas);
      setPerguntaEditandoId(null);
      setNovaPergunta({ texto: '', tipo: 'Aberta', alternativas: ['', ''], isCondicional: false, id_pergunta_condicional: null, id_alternativa_condicional: null });
      mostrarMensagem('success', 'Pergunta atualizada!');
    } else {
      // Adiciona nova pergunta
      setPerguntas([...perguntas, {
        ...novaPergunta,
        id: Date.now(),
        alternativas: (novaPergunta.tipo === 'Fechada' || novaPergunta.tipo === 'Escala Likert')
          ? novaPergunta.alternativas.filter(a => a.trim() !== '')
          : []
      }]);
      setNovaPergunta({ texto: '', tipo: 'Aberta', alternativas: ['', ''], isCondicional: false, id_pergunta_condicional: null, id_alternativa_condicional: null });
      mostrarMensagem('success', 'Pergunta adicionada à lista');
    }
  };

  const adicionarAlternativa = () => {
    setNovaPergunta({
      ...novaPergunta,
      alternativas: [...novaPergunta.alternativas, '']
    });
  };

  const removerAlternativa = (index) => {
    if (novaPergunta.alternativas.length <= 2) {
      mostrarMensagem('error', 'Mínimo de 2 alternativas');
      return;
    }
    const novasAlternativas = novaPergunta.alternativas.filter((_, i) => i !== index);
    setNovaPergunta({ ...novaPergunta, alternativas: novasAlternativas });
  };

  const atualizarAlternativa = (index, valor) => {
    const novasAlternativas = [...novaPergunta.alternativas];
    novasAlternativas[index] = valor;
    setNovaPergunta({ ...novaPergunta, alternativas: novasAlternativas });
  };

  const removerPergunta = (id) => {
    setPerguntas(perguntas.filter(p => p.id !== id));
    // Se estava editando esta pergunta, cancela a edição
    if (perguntaEditandoId === id) {
      setPerguntaEditandoId(null);
      setNovaPergunta({ texto: '', tipo: 'Aberta', alternativas: ['', ''], isCondicional: false, id_pergunta_condicional: null, id_alternativa_condicional: null });
    }
  };

  const editarPergunta = (pergunta) => {
    setPerguntaEditandoId(pergunta.id);
    setNovaPergunta({
      texto: pergunta.texto,
      tipo: pergunta.tipo,
      alternativas: pergunta.alternativas && pergunta.alternativas.length > 0
        ? [...pergunta.alternativas]
        : ['', ''],
      isCondicional: pergunta.isCondicional || false,
      id_pergunta_condicional: pergunta.id_pergunta_condicional || null,
      id_alternativa_condicional: pergunta.id_alternativa_condicional || null
    });
    // Scroll para o formulário de perguntas
    setTimeout(() => {
      const formulario = document.querySelector('.pergunta-form');
      if (formulario) {
        formulario.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const cancelarEdicaoPergunta = () => {
    setPerguntaEditandoId(null);
    setNovaPergunta({ texto: '', tipo: 'Aberta', alternativas: ['', ''], isCondicional: false, id_pergunta_condicional: null, id_alternativa_condicional: null });
  };

  const moverPerguntaParaCima = (index) => {
    if (index === 0) return; // Já está no topo
    const novasPerguntas = [...perguntas];
    [novasPerguntas[index - 1], novasPerguntas[index]] = [novasPerguntas[index], novasPerguntas[index - 1]];
    setPerguntas(novasPerguntas);
  };

  const moverPerguntaParaBaixo = (index) => {
    if (index === perguntas.length - 1) return; // Já está no final
    const novasPerguntas = [...perguntas];
    [novasPerguntas[index], novasPerguntas[index + 1]] = [novasPerguntas[index + 1], novasPerguntas[index]];
    setPerguntas(novasPerguntas);
  };

  const carregarQuestionarioParaEdicao = async (quest) => {
    try {
      setLoading(true);

      // Carregar dados do questionário
      setNovoQuestionario({
        titulo: quest.titulo || '',
        descricao: quest.descricao || '',
        perfil_alvo: quest.perfil_alvo ? quest.perfil_alvo.split(';').map(p => p.trim()) : [],
        classificacao: quest.classificacao || ''
      });

      // Carregar questionário com perguntas e alternativas - usar rota pública
      const response = await api.get(`/public/questionario/${quest.id_questionario}/perguntas`);
      const questionarioCompleto = response.data.data;
      const perguntasData = questionarioCompleto.perguntas || [];

      console.log('📊 Dados brutos recebidos:', perguntasData);

      // Criar mapa de ID do banco → ID temporário
      const mapIdBancoParaTemporario = {};

      // Mapear perguntas para o formato usado no componente
      const perguntasComAlternativas = perguntasData.map(pergunta => {
        // Normalizar tipo removendo espaços extras
        const tipoNormalizado = (pergunta.tipo_pergunta || '').trim();

        // Gerar ID temporário para esta pergunta
        const idTemporario = Date.now() + Math.random();
        mapIdBancoParaTemporario[pergunta.id_pergunta] = idTemporario;

        console.log('📥 Tipo da pergunta do banco:', {
          original: pergunta.tipo_pergunta,
          normalizado: tipoNormalizado,
          texto: pergunta.texto_pergunta,
          alternativas: pergunta.alternativas,
          id_pergunta_condicional: pergunta.id_pergunta_condicional,
          id_alternativa_condicional: pergunta.id_alternativa_condicional
        });

        return {
          id: idTemporario,
          id_original_banco: pergunta.id_pergunta, // Guardar ID original para mapear condições
          texto: pergunta.texto_pergunta,
          tipo: tipoNormalizado,
          alternativas: pergunta.alternativas?.map(a => a.texto_alternativa) || [],
          alternativas_com_id: pergunta.alternativas || [], // Guardar alternativas com ID
          isCondicional: !!pergunta.id_pergunta_condicional,
          id_pergunta_condicional_banco: pergunta.id_pergunta_condicional, // ID do banco, vamos mapear depois
          id_alternativa_condicional_banco: pergunta.id_alternativa_condicional // ID do banco, vamos mapear depois
        };
      });

      // Segunda passagem: mapear IDs de perguntas condicionais e índices de alternativas
      perguntasComAlternativas.forEach(pergunta => {
        if (pergunta.isCondicional && pergunta.id_pergunta_condicional_banco) {
          // Mapear ID da pergunta pai do banco para ID temporário
          pergunta.id_pergunta_condicional = mapIdBancoParaTemporario[pergunta.id_pergunta_condicional_banco];

          // Encontrar índice da alternativa
          if (pergunta.id_alternativa_condicional_banco) {
            const perguntaPai = perguntasComAlternativas.find(p => p.id_original_banco === pergunta.id_pergunta_condicional_banco);
            if (perguntaPai && perguntaPai.alternativas_com_id) {
              const indexAlternativa = perguntaPai.alternativas_com_id.findIndex(
                alt => alt.id_alternativa === pergunta.id_alternativa_condicional_banco
              );
              if (indexAlternativa !== -1) {
                pergunta.id_alternativa_condicional = indexAlternativa + 1; // 1-based
              }
            }
          }

          // Limpar campos temporários
          delete pergunta.id_pergunta_condicional_banco;
          delete pergunta.id_alternativa_condicional_banco;
          delete pergunta.id_original_banco;
          delete pergunta.alternativas_com_id;
        }
      });

      console.log('✅ Perguntas mapeadas final:', perguntasComAlternativas);

      setPerguntas(perguntasComAlternativas);
      setQuestionarioEditando(quest);
      setModoEdicao(true);

      mostrarMensagem('success', 'Questionário carregado para edição');

      // Scroll para o formulário
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Erro ao carregar questionário:', error);
      mostrarMensagem('error', tratarErroAPI(error, 'carregar', 'questionário'));
    } finally {
      setLoading(false);
    }
  };

  const cancelarEdicao = () => {
    setModoEdicao(false);
    setQuestionarioEditando(null);
    setNovoQuestionario({ titulo: '', descricao: '', perfil_alvo: [], classificacao: '' });
    setPerguntas([]);
    setNovaPergunta({ texto: '', tipo: 'Aberta', alternativas: ['', ''], isCondicional: false, id_pergunta_condicional: null, id_alternativa_condicional: null });
    setPerguntaEditandoId(null);
    mostrarMensagem('success', 'Edição cancelada');
  };

  const atualizarQuestionario = async () => {
    try {
      // Validações
      if (!novoQuestionario.titulo || !novoQuestionario.titulo.trim()) {
        mostrarMensagem('error', 'O título do questionário é obrigatório');
        return;
      }

      if (!novoQuestionario.descricao || !novoQuestionario.descricao.trim()) {
        mostrarMensagem('error', 'A descrição do questionário é obrigatória');
        return;
      }

      if (perguntas.length === 0) {
        mostrarMensagem('error', 'Adicione pelo menos uma pergunta');
        return;
      }

      setLoading(true);
      logarRequisicao('Atualizar', 'questionário', { novoQuestionario, perguntas });

      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user?.id_usuario || user?.id || 1;

      // Atualizar dados do questionário (sem cpf_criador - não é permitido na atualização)
      await api.put(`/questionario/${questionarioEditando.id_questionario}`, {
        id_usuario: userId,
        perfil_alvo: novoQuestionario.perfil_alvo.length > 0 ? novoQuestionario.perfil_alvo.join(';') : null,
        titulo: novoQuestionario.titulo.trim(),
        descricao: novoQuestionario.descricao.trim(),
        classificacao: novoQuestionario.classificacao || null
      });

      // Buscar e deletar perguntas antigas - usar rota pública para leitura
      const responseQuestionario = await api.get(`/public/questionario/${questionarioEditando.id_questionario}/perguntas`);
      const questionarioCompleto = responseQuestionario.data.data;
      const perguntasAntigasData = questionarioCompleto.perguntas || [];

      // IMPORTANTE: Deletar primeiro as perguntas CONDICIONAIS (que dependem de outras)
      // depois as perguntas NORMAIS (que não dependem de ninguém)
      const perguntasCondicionaisAntigas = perguntasAntigasData.filter(p => p.id_pergunta_condicional);
      const perguntasNormaisAntigas = perguntasAntigasData.filter(p => !p.id_pergunta_condicional);

      // Deletar condicionais primeiro
      for (const pergunta of perguntasCondicionaisAntigas) {
        await api.delete(`/pergunta/${pergunta.id_pergunta}`);
      }

      // Depois deletar normais
      for (const pergunta of perguntasNormaisAntigas) {
        await api.delete(`/pergunta/${pergunta.id_pergunta}`);
      }

      // Criar novas perguntas em duas etapas:
      // ETAPA 1: Criar todas as perguntas NÃO condicionais e suas alternativas
      const mapIdTemporarioParaReal = {}; // Mapear IDs temporários (Date.now()) para IDs reais do banco
      const mapAlternativaIndexParaId = {}; // Mapear {idPergunta: {1: idAlt1, 2: idAlt2, ...}}
      const perguntasCondicionais = []; // Guardar perguntas condicionais para criar depois

      for (let i = 0; i < perguntas.length; i++) {
        const pergunta = perguntas[i];

        // Se for condicional, guardar para criar depois
        if (pergunta.isCondicional && pergunta.id_pergunta_condicional) {
          perguntasCondicionais.push({ pergunta, ordem: i + 1 });
          continue;
        }

        const normalizedType = (pergunta.tipo || '').toString().trim();
        const enumValue = tipoPerguntaEnumMap[normalizedType] || normalizedType
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/\s+/g, '_');

        const perguntaData = {
          id_questionario: questionarioEditando.id_questionario,
          texto_pergunta: pergunta.texto,
          tipo_pergunta: enumValue || 'Aberta',
          ordem: i + 1,
          id_pergunta_condicional: null,
          id_alternativa_condicional: null
        };

        try {
          const responsePergunta = await api.post('/pergunta', perguntaData);
          const perguntaId = responsePergunta.data.data?.id_pergunta || responsePergunta.data.id_pergunta;

          if (!perguntaId) {
            throw new Error('ID da pergunta não retornado pela API');
          }

          // Mapear ID temporário para ID real
          mapIdTemporarioParaReal[pergunta.id] = perguntaId;

          if ((pergunta.tipo === 'Fechada' || pergunta.tipo === 'Escala Likert') && pergunta.alternativas && pergunta.alternativas.length > 0) {
            const responseAlternativas = await api.post('/alternativa/many', {
              id_pergunta: perguntaId,
              alternativas: pergunta.alternativas
            });

            // Capturar IDs das alternativas retornadas
            const alternativasCriadas = responseAlternativas.data;
            if (alternativasCriadas && Array.isArray(alternativasCriadas)) {
              mapAlternativaIndexParaId[perguntaId] = {};
              alternativasCriadas.forEach((alt, idx) => {
                mapAlternativaIndexParaId[perguntaId][idx + 1] = alt.id_alternativa;
              });
            }
          }
        } catch (perguntaError) {
          throw new Error(`Falha ao criar pergunta "${pergunta.texto}": ${perguntaError.message}`);
        }
      }

      // ETAPA 2: Criar perguntas condicionais
      for (const item of perguntasCondicionais) {
        const pergunta = item.pergunta;
        const normalizedType = (pergunta.tipo || '').toString().trim();
        const enumValue = tipoPerguntaEnumMap[normalizedType] || normalizedType
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/\s+/g, '_');

        // Mapear ID temporário da pergunta pai para ID real
        const id_pergunta_condicional_real = mapIdTemporarioParaReal[pergunta.id_pergunta_condicional];

        // Buscar ID real da alternativa
        let id_alternativa_condicional_real = null;
        if (id_pergunta_condicional_real && pergunta.id_alternativa_condicional) {
          const mapAlternativas = mapAlternativaIndexParaId[id_pergunta_condicional_real];
          if (mapAlternativas) {
            id_alternativa_condicional_real = mapAlternativas[pergunta.id_alternativa_condicional];
          }
        }

        const perguntaData = {
          id_questionario: questionarioEditando.id_questionario,
          texto_pergunta: pergunta.texto,
          tipo_pergunta: enumValue || 'Aberta',
          ordem: item.ordem,
          id_pergunta_condicional: id_pergunta_condicional_real,
          id_alternativa_condicional: id_alternativa_condicional_real
        };

        try {
          const responsePergunta = await api.post('/pergunta', perguntaData);
          const perguntaId = responsePergunta.data.data?.id_pergunta || responsePergunta.data.id_pergunta;

          if (!perguntaId) {
            throw new Error('ID da pergunta não retornado pela API');
          }

          // Mapear ID temporário para ID real (para perguntas condicionais em cascata)
          mapIdTemporarioParaReal[pergunta.id] = perguntaId;

          if ((pergunta.tipo === 'Fechada' || pergunta.tipo === 'Escala Likert') && pergunta.alternativas && pergunta.alternativas.length > 0) {
            const responseAlternativas = await api.post('/alternativa/many', {
              id_pergunta: perguntaId,
              alternativas: pergunta.alternativas
            });

            // Capturar IDs das alternativas retornadas
            const alternativasCriadas = responseAlternativas.data;
            if (alternativasCriadas && Array.isArray(alternativasCriadas)) {
              mapAlternativaIndexParaId[perguntaId] = {};
              alternativasCriadas.forEach((alt, idx) => {
                mapAlternativaIndexParaId[perguntaId][idx + 1] = alt.id_alternativa;
              });
            }
          }
        } catch (perguntaError) {
          throw new Error(`Falha ao criar pergunta condicional "${pergunta.texto}": ${perguntaError.message}`);
        }
      }

      mostrarMensagem('success', '✅ Alterações salvas com sucesso!');

      // Limpar formulário e sair do modo edição
      cancelarEdicao();

      // Recarregar lista
      await carregarQuestionarios();
    } catch (error) {
      console.error('Erro ao atualizar questionário:', error);
      mostrarMensagem('error', tratarErroAPI(error, 'atualizar', 'questionário'));
    } finally {
      setLoading(false);
    }
  };

  const criarQuestionario = async () => {
    try {
      // Validações
      if (!novoQuestionario.titulo || !novoQuestionario.titulo.trim()) {
        mostrarMensagem('error', 'O título do questionário é obrigatório');
        return;
      }

      if (!novoQuestionario.descricao || !novoQuestionario.descricao.trim()) {
        mostrarMensagem('error', 'A descrição do questionário é obrigatória');
        return;
      }

      if (perguntas.length === 0) {
        mostrarMensagem('error', 'Adicione pelo menos uma pergunta');
        return;
      }

      setLoading(true);
      logarRequisicao('Criar', 'questionário', { novoQuestionario, perguntas });

      // Pegar ID e CPF do usuário logado
      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user?.id_usuario || user?.id || 1; // fallback para 1 se não encontrar
      const userCpf = user?.cpf || null;

      // Criar questionário
      const responseQuestionario = await api.post('/questionario', {
        id_usuario: userId,
        perfil_alvo: novoQuestionario.perfil_alvo.length > 0 ? novoQuestionario.perfil_alvo.join(';') : null,
        titulo: novoQuestionario.titulo,
        descricao: novoQuestionario.descricao,
        cpf_criador: userCpf,
        classificacao: novoQuestionario.classificacao || null
      });

      logarSucesso('Criar', 'questionário', responseQuestionario.data);
      const questionarioId = responseQuestionario.data.data.id_questionario;

      console.log('ID do questionário criado:', questionarioId);
      console.log('Perguntas a serem criadas:', perguntas);

      // Criar perguntas em duas etapas:
      // ETAPA 1: Criar todas as perguntas NÃO condicionais e suas alternativas
      const mapIdTemporarioParaReal = {}; // Mapear IDs temporários (Date.now()) para IDs reais do banco
      const mapAlternativaIndexParaId = {}; // Mapear {idPergunta: {1: idAlt1, 2: idAlt2, ...}}
      const perguntasCondicionais = []; // Guardar perguntas condicionais para criar depois

      for (let i = 0; i < perguntas.length; i++) {
        const pergunta = perguntas[i];

        // Se for condicional, guardar para criar depois
        if (pergunta.isCondicional && pergunta.id_pergunta_condicional) {
          perguntasCondicionais.push({ pergunta, ordem: i + 1 });
          continue;
        }

        const normalizedType = (pergunta.tipo || '')
          .toString()
          .trim();
        const enumValue = tipoPerguntaEnumMap[normalizedType]
          || normalizedType
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/\s+/g, '_');

        const perguntaData = {
          id_questionario: questionarioId,
          texto_pergunta: pergunta.texto,
          tipo_pergunta: enumValue || 'Aberta',
          ordem: i + 1,
          id_pergunta_condicional: null,
          id_alternativa_condicional: null
        };

        try {
          const responsePergunta = await api.post('/pergunta', perguntaData);

          const perguntaId = responsePergunta.data.data?.id_pergunta || responsePergunta.data.id_pergunta;

          if (!perguntaId) {
            throw new Error('ID da pergunta não retornado pela API');
          }

          // Mapear ID temporário para ID real
          mapIdTemporarioParaReal[pergunta.id] = perguntaId;

          // Se for pergunta fechada ou Escala Likert, criar as alternativas
          if ((pergunta.tipo === 'Fechada' || pergunta.tipo === 'Escala Likert') && pergunta.alternativas && pergunta.alternativas.length > 0) {
            const responseAlternativas = await api.post('/alternativa/many', {
              id_pergunta: perguntaId,
              alternativas: pergunta.alternativas
            });

            // Capturar IDs das alternativas retornadas
            const alternativasCriadas = responseAlternativas.data;
            if (alternativasCriadas && Array.isArray(alternativasCriadas)) {
              mapAlternativaIndexParaId[perguntaId] = {};
              alternativasCriadas.forEach((alt, idx) => {
                mapAlternativaIndexParaId[perguntaId][idx + 1] = alt.id_alternativa;
              });
            }
          }
        } catch (perguntaError) {
          mostrarMensagem('error', tratarErroAPI(perguntaError, 'criar', 'pergunta'));
          return;
        }
      }

      // ETAPA 2: Criar perguntas condicionais
      for (const item of perguntasCondicionais) {
        const pergunta = item.pergunta;
        const normalizedType = (pergunta.tipo || '')
          .toString()
          .trim();
        const enumValue = tipoPerguntaEnumMap[normalizedType]
          || normalizedType
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/\s+/g, '_');

        // Mapear ID temporário da pergunta pai para ID real
        const id_pergunta_condicional_real = mapIdTemporarioParaReal[pergunta.id_pergunta_condicional];

        // Buscar ID real da alternativa
        let id_alternativa_condicional_real = null;
        if (id_pergunta_condicional_real && pergunta.id_alternativa_condicional) {
          const mapAlternativas = mapAlternativaIndexParaId[id_pergunta_condicional_real];
          if (mapAlternativas) {
            id_alternativa_condicional_real = mapAlternativas[pergunta.id_alternativa_condicional];
          }
        }

        const perguntaData = {
          id_questionario: questionarioId,
          texto_pergunta: pergunta.texto,
          tipo_pergunta: enumValue || 'Aberta',
          ordem: item.ordem,
          id_pergunta_condicional: id_pergunta_condicional_real,
          id_alternativa_condicional: id_alternativa_condicional_real
        };

        try {
          const responsePergunta = await api.post('/pergunta', perguntaData);

          const perguntaId = responsePergunta.data.data?.id_pergunta || responsePergunta.data.id_pergunta;

          if (!perguntaId) {
            throw new Error('ID da pergunta não retornado pela API');
          }

          // Mapear ID temporário para ID real (para perguntas condicionais em cascata)
          mapIdTemporarioParaReal[pergunta.id] = perguntaId;

          // Se for pergunta fechada ou Escala Likert, criar as alternativas
          if ((pergunta.tipo === 'Fechada' || pergunta.tipo === 'Escala Likert') && pergunta.alternativas && pergunta.alternativas.length > 0) {
            const responseAlternativas = await api.post('/alternativa/many', {
              id_pergunta: perguntaId,
              alternativas: pergunta.alternativas
            });

            const alternativasCriadas = responseAlternativas.data;
            if (alternativasCriadas && Array.isArray(alternativasCriadas)) {
              mapAlternativaIndexParaId[perguntaId] = {};
              alternativasCriadas.forEach((alt, idx) => {
                mapAlternativaIndexParaId[perguntaId][idx + 1] = alt.id_alternativa;
              });
            }
          }
        } catch (perguntaError) {
          mostrarMensagem('error', tratarErroAPI(perguntaError, 'criar', 'pergunta condicional'));
          return;
        }
      }

      mostrarMensagem('success', 'Questionário criado com sucesso!');

      // Limpar formulário
      setNovoQuestionario({ titulo: '', descricao: '', perfil_alvo: [] });
      setPerguntas([]);

      // Recarregar lista
      carregarQuestionarios();
    } catch (error) {
      mostrarMensagem('error', tratarErroAPI(error, 'criar', 'questionário'));
    } finally {
      setLoading(false);
    }
  };

  const alternarStatusQuestionario = async (quest) => {
    try {
      setLoading(true);
      const novoStatus = !quest.ativo;
      const acao = novoStatus ? 'ativar' : 'desativar';

      logarRequisicao(acao, 'questionário', { id: quest.id_questionario, novoStatus });

      await api.put(`/questionario/${quest.id_questionario}`, {
        ativo: novoStatus
      });

      // Atualizar lista local
      setQuestionarios(questionarios.map(q =>
        q.id_questionario === quest.id_questionario
          ? { ...q, ativo: novoStatus }
          : q
      ));

      mostrarMensagem('success', `Questionário ${novoStatus ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (error) {
      mostrarMensagem('error', tratarErroAPI(error, 'alterar status do', 'questionário'));
    } finally {
      setLoading(false);
    }
  };

  const excluirQuestionario = async (id) => {
    if (!window.confirm('Deseja realmente excluir este questionário?')) return;

    try {
      setLoading(true);
      logarRequisicao('Excluir', 'questionário', { id });
      const response = await api.delete(`/questionario/${id}`);
      logarSucesso('Excluir', 'questionário', response.data);

      setQuestionarios(questionarios.filter(q => q.id_questionario !== id));
      mostrarMensagem('success', 'Questionário excluído com sucesso!');
    } catch (error) {
      mostrarMensagem('error', tratarErroAPI(error, 'excluir', 'questionário'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="questionario-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}>
          <i className="fas fa-arrow-left"></i>
        </button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>📋 Gerenciar Questionários Flexíveis</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      {canCreateQuestionario && (
        <div className="questionario-form">
        <h2>{modoEdicao ? '✏️ Editar Questionário' : '📝 Criar Novo Questionário'}</h2>
        {modoEdicao && (
          <div className="modo-edicao-badge">
            <span>Editando: {questionarioEditando?.titulo || 'Sem título'}</span>
            <button className="btn-cancelar-edicao" onClick={cancelarEdicao} disabled={loading}>
              ✕ Cancelar edição
            </button>
          </div>
        )}

        <div className="form-section">
          <div className="form-group">
            <label>Título:</label>
            <input
              type="text"
              value={novoQuestionario.titulo}
              onChange={(e) => setNovoQuestionario({ ...novoQuestionario, titulo: e.target.value })}
              placeholder="Ex: Pesquisa de satisfação"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Descrição:</label>
            <input
              type="text"
              value={novoQuestionario.descricao}
              onChange={(e) => setNovoQuestionario({ ...novoQuestionario, descricao: e.target.value })}
              placeholder="Descrição do questionário"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Classificação:</label>
            <select
              value={novoQuestionario.classificacao}
              onChange={(e) => setNovoQuestionario({ ...novoQuestionario, classificacao: e.target.value })}
              disabled={loading}
            >
              {opcoesClassificacao.map((opcao) => (
                <option key={opcao.valor} value={opcao.valor}>
                  {opcao.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-section">
          <label>Perfil alvo (selecione um ou mais):</label>
          <div className="perfil-checkboxes">
            {opcoesPerfil.map((perfil) => (
              <label key={perfil} className="perfil-checkbox-item">
                <input
                  type="checkbox"
                  value={perfil}
                  checked={novoQuestionario.perfil_alvo.includes(perfil)}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newPerfis = e.target.checked
                      ? [...novoQuestionario.perfil_alvo, value]
                      : novoQuestionario.perfil_alvo.filter(p => p !== value);
                    setNovoQuestionario({ ...novoQuestionario, perfil_alvo: newPerfis });
                  }}
                  disabled={loading}
                />
                <span>{perfil}</span>
              </label>
            ))}
          </div>
          {novoQuestionario.perfil_alvo.length > 0 && (
            <div className="perfis-selecionados">
              <small><strong>Selecionados ({novoQuestionario.perfil_alvo.length}):</strong> {novoQuestionario.perfil_alvo.join(', ')}</small>
            </div>
          )}
        </div>

        <div className="perguntas-section">
          <h3>Perguntas ({perguntas.length}/20)</h3>

          <div className="pergunta-form">
            <label>Texto da Pergunta:</label>
            <input
              type="text"
              value={novaPergunta.texto}
              onChange={(e) => setNovaPergunta({ ...novaPergunta, texto: e.target.value })}
              placeholder="Digite sua pergunta aqui"
              disabled={loading}
            />

            <label>Tipo:</label>
            <select
              value={novaPergunta.tipo}
              onChange={(e) => {
                const novoTipo = e.target.value;
                if (novoTipo === 'Escala Likert') {
                  // Preencher automaticamente com alternativas Likert
                  setNovaPergunta({
                    ...novaPergunta,
                    tipo: novoTipo,
                    alternativas: [...alternativasLikert]
                  });
                } else {
                  setNovaPergunta({
                    ...novaPergunta,
                    tipo: novoTipo,
                    alternativas: novoTipo === 'Fechada' ? ['', ''] : []
                  });
                }
              }}
              disabled={loading}
            >
              <option value="Aberta">Aberta (resposta livre)</option>
              <option value="Fechada">Fechada (múltipla escolha)</option>
              <option value="Escala Likert">Escala Likert (1 a 5 com emojis)</option>
            </select>

            {perguntas.length > 0 && (
              <div className="condicional-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={novaPergunta.isCondicional}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setNovaPergunta({
                        ...novaPergunta,
                        isCondicional: isChecked,
                        id_pergunta_condicional: isChecked ? novaPergunta.id_pergunta_condicional : null,
                        id_alternativa_condicional: isChecked ? novaPergunta.id_alternativa_condicional : null
                      });
                    }}
                    disabled={loading}
                  />
                  <span>Esta pergunta é condicional? (aparece apenas se outra pergunta for respondida)</span>
                </label>

                {novaPergunta.isCondicional && (
                  <div className="condicional-config">
                    <label>Depende da pergunta:</label>
                    <select
                      value={novaPergunta.id_pergunta_condicional || ''}
                      onChange={(e) => {
                        const perguntaId = e.target.value;
                        setNovaPergunta({
                          ...novaPergunta,
                          id_pergunta_condicional: perguntaId || null,
                          id_alternativa_condicional: null // Reset alternativa quando muda pergunta
                        });
                      }}
                      disabled={loading}
                    >
                      <option value="">Selecione uma pergunta...</option>
                      {perguntas
                        .filter(p => (p.tipo === 'Fechada' || p.tipo === 'Escala Likert') && p.alternativas && p.alternativas.length > 0)
                        .filter(p => perguntaEditandoId ? p.id !== perguntaEditandoId : true) // Não mostrar a própria pergunta se estiver editando
                        .map((p, index) => (
                          <option key={p.id} value={p.id}>
                            {index + 1}. {p.texto} ({p.tipo})
                          </option>
                        ))}
                    </select>

                    {novaPergunta.id_pergunta_condicional && (
                      <>
                        <label>Se a resposta for:</label>
                        <select
                          value={novaPergunta.id_alternativa_condicional || ''}
                          onChange={(e) => {
                            setNovaPergunta({
                              ...novaPergunta,
                              id_alternativa_condicional: e.target.value ? parseInt(e.target.value) : null
                            });
                          }}
                          disabled={loading}
                        >
                          <option value="">Selecione uma alternativa...</option>
                          {(() => {
                            const perguntaSelecionada = perguntas.find(p => String(p.id) === String(novaPergunta.id_pergunta_condicional));
                            return perguntaSelecionada?.alternativas?.map((alt, idx) => (
                              <option key={idx} value={idx + 1}>
                                {alt}
                              </option>
                            )) || [];
                          })()}
                        </select>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {novaPergunta.tipo === 'Escala Likert' && (
              <div className="alternativas-section">
                <label>Alternativas (Escala Likert - Fixas):</label>
                <div className="likert-preview">
                  {novaPergunta.alternativas.map((alt, index) => (
                    <div key={index} className="likert-option">
                      <span className="likert-emoji">{alt}</span>
                    </div>
                  ))}
                </div>
                <small style={{ color: '#666', fontStyle: 'italic' }}>
                  As alternativas da Escala Likert são fixas e não podem ser editadas.
                </small>
              </div>
            )}

            {novaPergunta.tipo === 'Fechada' && (
              <div className="alternativas-section">
                <label>Alternativas:</label>
                {novaPergunta.alternativas.map((alt, index) => (
                  <div key={index} className="alternativa-input">
                    <span className="alternativa-letra">{String.fromCharCode(65 + index)})</span>
                    <input
                      type="text"
                      value={alt}
                      onChange={(e) => atualizarAlternativa(index, e.target.value)}
                      placeholder={`Alternativa ${String.fromCharCode(65 + index)}`}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="btn-remove-alternativa"
                      onClick={() => removerAlternativa(index)}
                      disabled={loading || novaPergunta.alternativas.length <= 2}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-add-alternativa"
                  onClick={adicionarAlternativa}
                  disabled={loading || novaPergunta.alternativas.length >= 6}
                >
                  + Adicionar alternativa
                </button>
              </div>
            )}

            <div className="btn-group-pergunta">
              <button
                className="btn btn-add-pergunta"
                onClick={adicionarPergunta}
                disabled={loading}
              >
                {perguntaEditandoId ? '✓ Salvar Alterações' : '+ Adicionar Pergunta'}
              </button>
              {perguntaEditandoId && (
                <button
                  className="btn btn-cancelar"
                  onClick={cancelarEdicaoPergunta}
                  disabled={loading}
                >
                  ✕ Cancelar Edição
                </button>
              )}
            </div>
          </div>

          <div className="perguntas-lista">
            {console.log('📋 Total de perguntas a renderizar:', perguntas.length, perguntas)}
            {perguntas.map((pergunta, index) => (
              <div key={pergunta.id} className="pergunta-item">
                {console.log(`📝 Pergunta ${index + 1}:`, { texto: pergunta.texto, tipo: pergunta.tipo, alternativas: pergunta.alternativas })}
                <div className="pergunta-header">
                  <span className="pergunta-numero">{index + 1}.</span>
                  <span className="pergunta-texto">{pergunta.texto}</span>
                  <span className="pergunta-tipo">({pergunta.tipo})</span>
                  <div className="pergunta-acoes">
                    <button
                      className="btn-move-pergunta"
                      onClick={() => moverPerguntaParaCima(index)}
                      disabled={loading || index === 0}
                      title="Mover para cima"
                    >
                      ▲
                    </button>
                    <button
                      className="btn-move-pergunta"
                      onClick={() => moverPerguntaParaBaixo(index)}
                      disabled={loading || index === perguntas.length - 1}
                      title="Mover para baixo"
                    >
                      ▼
                    </button>
                    <button
                      className="btn-edit-pergunta"
                      onClick={() => editarPergunta(pergunta)}
                      disabled={loading}
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-remove-pergunta"
                      onClick={() => removerPergunta(pergunta.id)}
                      disabled={loading}
                      title="Remover"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {console.log('🎯 Verificando condição:', {
                  tipo: pergunta.tipo,
                  isFechada: pergunta.tipo === 'Fechada',
                  isLikert: pergunta.tipo === 'Escala Likert',
                  temAlternativas: !!pergunta.alternativas,
                  qtdAlternativas: pergunta.alternativas?.length || 0,
                  condicaoAtendida: (pergunta.tipo === 'Fechada' || pergunta.tipo === 'Escala Likert') && pergunta.alternativas && pergunta.alternativas.length > 0
                })}
                {(pergunta.tipo === 'Fechada' || pergunta.tipo === 'Escala Likert') && pergunta.alternativas && pergunta.alternativas.length > 0 && (
                  <div className={`pergunta-alternativas ${pergunta.tipo.trim() === 'Escala Likert' ? 'likert-horizontal' : ''}`}>
                    {console.log('🔍 RENDERIZANDO ALTERNATIVAS:', {
                      tipo: pergunta.tipo,
                      tipoTrimmed: pergunta.tipo.trim(),
                      isLikert: pergunta.tipo.trim() === 'Escala Likert',
                      className: `pergunta-alternativas ${pergunta.tipo.trim() === 'Escala Likert' ? 'likert-horizontal' : ''}`
                    })}
                    {pergunta.alternativas.map((alt, i) => (
                      <span key={i} className="alternativa-badge">
                        {pergunta.tipo === 'Escala Likert' ? alt : `${String.fromCharCode(65 + i)}) ${alt}`}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button
            className="btn btn-success"
            onClick={modoEdicao ? atualizarQuestionario : criarQuestionario}
            disabled={loading}
          >
            {loading ? '⏳ Processando...' : (modoEdicao ? '✓ Salvar alterações' : '✓ Criar questionário')}
          </button>
          {modoEdicao ? (
            <button
              className="btn btn-secondary"
              onClick={cancelarEdicao}
              disabled={loading}
            >
              ✕ Cancelar edição
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setNovoQuestionario({ titulo: '', descricao: '', perfil_alvo: [], classificacao: '' });
                setPerguntas([]);
              }}
              disabled={loading}
            >
              ✕ Limpar
            </button>
          )}
        </div>
      </div>
      )}

      <div className="questionarios-lista-section">
        <h2>Questionários Criados</h2>
        {loading && <p>Carregando...</p>}
        {!loading && questionarios.length === 0 && (
          <p className="empty-message">Nenhum questionário criado ainda</p>
        )}

        <div className="questionarios-grid">
          {questionarios.map((quest) => (
            <div key={quest.id_questionario} className="questionario-card">
              <div className="card-header">
                <h3>{quest.titulo || 'Sem título'} {quest.ativo ? <span style={{ fontSize: '0.8rem', color: 'green' }}>(Ativo)</span> : <span style={{ fontSize: '0.8rem', color: 'red' }}>(Inativo)</span>}</h3>
                {quest.classificacao && (
                  <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      backgroundColor: quest.classificacao === 'Técnico' ? '#e3f2fd' : quest.classificacao === 'Administrativo' ? '#f3e5f5' : '#e8f5e9',
                      color: quest.classificacao === 'Técnico' ? '#1565c0' : quest.classificacao === 'Administrativo' ? '#6a1b9a' : '#2e7d32'
                    }}>
                      {quest.classificacao === 'Técnico' ? '🔧' : quest.classificacao === 'Administrativo' ? '📊' : '👥'} {quest.classificacao}
                    </span>
                  </div>
                )}
                {canCreateQuestionario && (
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                      className="btn-edit-card"
                      onClick={() => carregarQuestionarioParaEdicao(quest)}
                      disabled={loading}
                      style={{
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        padding: '8px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Editar"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-toggle-status"
                      onClick={() => alternarStatusQuestionario(quest)}
                      disabled={loading}
                      style={{
                        backgroundColor: quest.ativo ? '#ffc107' : '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '8px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title={quest.ativo ? 'Desativar' : 'Ativar'}
                    >
                      <i className={`fas fa-${quest.ativo ? 'ban' : 'check'}`}></i>
                    </button>
                    <button
                      className="btn-delete-card"
                      onClick={(e) => {
                        e.stopPropagation();
                        excluirQuestionario(quest.id_questionario);
                      }}
                      disabled={loading}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Excluir"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
              <div className="card-content">
                <p><strong>Descrição:</strong> {quest.descricao || 'Sem descrição'}</p>
                {quest.data_criacao && (
                  <p><strong>Data de criação:</strong> {new Date(quest.data_criacao).toLocaleDateString('pt-BR')}</p>
                )}
                <div className="perfil-alvo-display">
                  <strong>Perfil alvo:</strong>
                  {quest.perfil_alvo ? (
                    <div className="perfil-tags">
                      {quest.perfil_alvo.split(';').map((perfil, idx) => (
                        <span key={idx} className="perfil-tag">{perfil.trim()}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="perfil-tag perfil-todos">Todos</span>
                  )}
                </div>
                <p><strong>Criado por:</strong> {quest.nome_criador || `Usuário ID ${quest.id_usuario}`}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuestionarioManager;
